import { useEffect, useMemo, useState } from "react";
import type { Assignment, BeachMatchCandidate, Person, Project, Opportunity } from "../services/api";
import { createAssignment, listAssignments, listPeople, listProjects, triggerBeachMatch, getBeachMatchResults, updateProject, createProject, listOpportunities } from "../services/api";
import { getErrorAlertMessage } from "../utils/errors";
import { Modal } from "./Modal";
import type { AiProjectSummary } from "../services/api";
import { useAuth } from "../auth/AuthContext";
import { PROJECT_STATUS_OPTIONS, PROJECT_STATUSES } from "../constants/projectStatus";

export function ProjectsList() {
  const { hasRole } = useAuth();
  const canEdit = hasRole("admin") || hasRole("ops") || hasRole("root");
  const [projects, setProjects] = useState<Project[]>([]);
  const [sort, setSort] = useState<{ field: "project_id" | "title" | "status" | "start_date" | "end_date" | "delivery_unit"; direction: "asc" | "desc" }>({
    field: "project_id",
    direction: "asc",
  });
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [editDraft, setEditDraft] = useState<Project | null>(null);
  const [skillsText, setSkillsText] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [beachMatches, setBeachMatches] = useState<BeachMatchCandidate[]>([]);
  const [matchStatus, setMatchStatus] = useState<"IDLE" | "PENDING" | "RUNNING" | "COMPLETED" | "FAILED" | "NOT_FOUND">("IDLE");
  const [matchError, setMatchError] = useState<string | null>(null);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [showBeachModal, setShowBeachModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    void refresh();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const [projectData, assignmentData, peopleData, opportunitiesData] = await Promise.all([
        listProjects(),
        listAssignments(),
        listPeople(),
        listOpportunities(),
      ]);
      setProjects(projectData);
      setAssignments(assignmentData);
      setPeople(peopleData);
      setOpportunities(opportunitiesData);
    } catch (err) {
      setError(getErrorAlertMessage("load AWS requisitions", err));
    } finally {
      setLoading(false);
    }
  }

  const statusOptions = useMemo(() => {
    const unique = new Set<string>(PROJECT_STATUSES);
    projects.forEach((project) => {
      if (project.status) {
        unique.add(project.status);
      }
    });
    return Array.from(unique).sort();
  }, [projects]);

  const filteredProjects = useMemo(() => {
    return projects.filter((project) => {
      if (statusFilter && project.status !== statusFilter) {
        return false;
      }
      if (showActiveOnly && (project.status ?? "").toUpperCase() === "EXPIRED") {
        return false;
      }
      return true;
    });
  }, [projects, statusFilter, showActiveOnly]);

  const sortedProjects = useMemo(() => {
    const copy = [...filteredProjects];
    const { field, direction } = sort;
    copy.sort((a, b) => {
      const aValue = (a[field] ?? "") as string;
      const bValue = (b[field] ?? "") as string;
      const aNorm = typeof aValue === "string" ? aValue.toLowerCase() : String(aValue);
      const bNorm = typeof bValue === "string" ? bValue.toLowerCase() : String(bValue);
      if (aNorm === bNorm) return 0;
      const result = aNorm > bNorm ? 1 : -1;
      return direction === "asc" ? result : -result;
    });
    return copy;
  }, [filteredProjects, sort]);

  function toggleSort(field: typeof sort.field) {
    setSort((prev) => {
      if (prev.field === field) {
        return { field, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { field, direction: "asc" };
    });
  }

  useEffect(() => {
    if (selectedProject) {
      setEditDraft(selectedProject);
      setSkillsText(formatSkillsText(selectedProject));
      // Check if there's an existing beach match result
      void checkBeachMatchStatus();
    } else {
      setEditDraft(null);
      setSkillsText("");
    }
    setIsEditing(false);
    setModalError(null);
  }, [selectedProject]); // eslint-disable-line react-hooks/exhaustive-deps

  async function checkBeachMatchStatus() {
    if (!selectedProject) return;

    try {
      const result = await getBeachMatchResults(selectedProject.project_id);
      setMatchStatus(result.status);
      console.log("Beach match status:", result.status);
    } catch (err) {
      // If no results found (404), that's okay - just means no search has been run
      setMatchStatus("IDLE");
    }
  }

  function handleOpenProject(project: Project) {
    setSelectedProject(project);
  }

  function handleCloseModal() {
    setSelectedProject(null);
    setEditDraft(null);
    setModalError(null);
    setIsEditing(false);
    setBeachMatches([]);
    setMatchStatus("IDLE");
    setMatchError(null);
    setSearchPerformed(false);
  }

  async function handleTriggerBeachMatch() {
    if (!selectedProject) return;

    setMatchStatus("PENDING");
    setMatchError(null);
    setBeachMatches([]);
    setSearchPerformed(false);

    try {
      console.log("Triggering async beach match for project:", selectedProject.project_id);
      await triggerBeachMatch(selectedProject.project_id);
      console.log("Beach match triggered successfully");
      setMatchError("Beach matching started. Click refresh to check progress.");
    } catch (err) {
      console.error("Failed to trigger beach matching:", err);
      setMatchError(getErrorAlertMessage("start beach matching", err));
      setMatchStatus("FAILED");
    }
  }

  async function handleRefreshStatus() {
    await checkBeachMatchStatus();
  }

  async function handleShowBeachCandidates() {
    if (!selectedProject) return;

    try {
      const result = await getBeachMatchResults(selectedProject.project_id);
      setBeachMatches(result.matches || []);
      setSearchPerformed(true);
      setCurrentPage(0); // Reset to first page
      setShowBeachModal(true); // Open the modal
      if (result.message) {
        setMatchError(result.message);
      }
    } catch (err) {
      console.error("Failed to load beach match results:", err);
      setMatchError(getErrorAlertMessage("load beach candidates", err));
    }
  }

  function handleCloseBeachModal() {
    setShowBeachModal(false);
    setCurrentPage(0);
  }

  function handleNextCandidate() {
    if (currentPage < beachMatches.length - 1) {
      setCurrentPage(currentPage + 1);
    }
  }

  function handlePreviousCandidate() {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  }

  async function handleNominateCandidate(email: string) {
    if (!selectedProject) return;

    try {
      await createAssignment({
        project_id: selectedProject.project_id,
        person_email: email,
        status: "NOMINATED",
        notes: "Nominated via Beach Candidate Matching",
      });

      // Refresh assignments to show new nomination
      await refresh();

      // Remove candidate from matches list
      const newMatches = beachMatches.filter((m) => m.employee_email !== email);
      setBeachMatches(newMatches);

      // Adjust current page if necessary
      if (newMatches.length === 0) {
        // No more candidates - close modal
        handleCloseBeachModal();
      } else if (currentPage >= newMatches.length) {
        // Current page is now out of bounds, go to last page
        setCurrentPage(newMatches.length - 1);
      }

      // Show success message
      setMatchError(`Successfully nominated ${email}`);
      setTimeout(() => setMatchError(null), 3000);
    } catch (err) {
      setMatchError(getErrorAlertMessage("nominate candidate", err));
    }
  }

  function handleDraftChange(field: keyof Project, value: string | number | null | undefined) {
    setEditDraft((prev) => (prev ? { ...prev, [field]: value ?? undefined } : prev));
  }

  async function handleProjectSave() {
    if (!editDraft) return;
    setSaving(true);
    setModalError(null);
    const estimatedSkills = skillsText
      .split(",")
      .map((skill) => skill.trim())
      .filter((skill) => skill.length > 0);

    const payload: Partial<Project> = {
      title: editDraft.title,
      status: editDraft.status,
      start_date: editDraft.start_date,
      end_date: editDraft.end_date,
      delivery_unit: editDraft.delivery_unit,
      aws_role: editDraft.aws_role,
      aws_grade: editDraft.aws_grade,
      tw_role: editDraft.tw_role,
      tw_grade: editDraft.tw_grade,
      tw_archetype: editDraft.tw_archetype,
      requested_hours: editDraft.requested_hours,
      notes: editDraft.notes,
      estimated_skills: estimatedSkills,
      opportunity_id: editDraft.opportunity_id,
    };

    try {
      const updated = await updateProject(editDraft.project_id, payload);
      setProjects((prev) => prev.map((proj) => (proj.project_id === updated.project_id ? updated : proj)));
      setSelectedProject(updated);
      setEditDraft(updated);
      setSkillsText(formatSkillsText(updated));
      setIsEditing(false);
    } catch (err) {
      setModalError(getErrorAlertMessage("update this requisition", err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="layout single-column">
      <div className="panel">
        <div className="panel-header">
          <h2>AWS Requisition Records</h2>
          <div style={{ display: "flex", gap: "0.75rem" }}>
            {canEdit && (
              <button type="button" className="primary" onClick={() => setShowCreateModal(true)}>
                Create RR
              </button>
            )}
            <button type="button" className="ghost" onClick={() => void refresh()} disabled={loading}>
              {loading ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </div>
        <div className="form-grid">
          <label>
            Status Filter
            <select
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value);
              }}
            >
              <option value="">All statuses</option>
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </label>
          <label
            className="checkbox-field"
            style={{ flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center" }}
          >
            <span style={{ marginBottom: "0.25rem" }}>All Active</span>
            <input
              type="checkbox"
              checked={showActiveOnly}
              onChange={(event) => setShowActiveOnly(event.target.checked)}
            />
          </label>
        </div>
        {error && <div className="alert error">{error}</div>}
        {loading ? (
          <div className="panel">Loading AWS requisitions…</div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th onClick={() => toggleSort("project_id")} className="sortable">
                    RR {sort.field === "project_id" ? (sort.direction === "asc" ? "▲" : "▼") : ""}
                  </th>
                  <th onClick={() => toggleSort("title")} className="sortable">
                    Title {sort.field === "title" ? (sort.direction === "asc" ? "▲" : "▼") : ""}
                  </th>
                  <th onClick={() => toggleSort("status")} className="sortable">
                    Status {sort.field === "status" ? (sort.direction === "asc" ? "▲" : "▼") : ""}
                  </th>
                  <th onClick={() => toggleSort("start_date")} className="sortable">
                    Start Date {sort.field === "start_date" ? (sort.direction === "asc" ? "▲" : "▼") : ""}
                  </th>
                  <th onClick={() => toggleSort("end_date")} className="sortable">
                    End Date {sort.field === "end_date" ? (sort.direction === "asc" ? "▲" : "▼") : ""}
                  </th>
                  <th onClick={() => toggleSort("delivery_unit")} className="sortable">
                    Delivery Unit {sort.field === "delivery_unit" ? (sort.direction === "asc" ? "▲" : "▼") : ""}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedProjects.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="empty">
                      No requisitions found.
                    </td>
                  </tr>
                ) : (
                  sortedProjects.map((project) => (
                    <tr key={project.project_id} onClick={() => handleOpenProject(project)} className="clickable-row">
                      <td>{project.project_id}</td>
                      <td>{project.title}</td>
                      <td>{project.status ?? "-"}</td>
                      <td>{project.start_date ?? "-"}</td>
                      <td>{project.end_date ?? "-"}</td>
                      <td>{project.delivery_unit ?? "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <Modal
        open={Boolean(selectedProject)}
        onClose={handleCloseModal}
        title={selectedProject ? `${selectedProject.title} (${selectedProject.project_id})` : undefined}
      >
        {selectedProject && (
          <>
            {canEdit && (
              <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", marginBottom: "1rem" }}>
                <button type="button" className="ghost" onClick={() => setIsEditing((prev) => !prev)}>
                  {isEditing ? "View Details" : "Edit Details"}
                </button>
                <div style={{ width: "1.5rem" }} />
                <button
                  type="button"
                  className="primary"
                  onClick={handleTriggerBeachMatch}
                  disabled={matchStatus === "PENDING" || matchStatus === "RUNNING"}
                >
                  Search Beach
                </button>
                <button
                  type="button"
                  className="ghost"
                  onClick={handleRefreshStatus}
                  title="Refresh beach match status"
                  style={{
                    padding: "0.5rem 1rem",
                    minWidth: "auto",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
                  </svg>
                </button>
                {matchStatus === "PENDING" && <span style={{ color: "#0b7285", fontSize: "0.9rem", marginLeft: "0.5rem" }}>⏳ Pending...</span>}
                {matchStatus === "RUNNING" && <span style={{ color: "#0b7285", fontSize: "0.9rem", marginLeft: "0.5rem" }}>🔄 Running...</span>}
                {matchStatus === "COMPLETED" && (
                  <button
                    type="button"
                    className="primary"
                    onClick={handleShowBeachCandidates}
                  >
                    Show Beach Candidates
                  </button>
                )}
                {matchStatus === "FAILED" && <span style={{ color: "#b91c1c", fontSize: "0.9rem", marginLeft: "0.5rem" }}>❌ Failed</span>}
              </div>
            )}
            {modalError && <div className="alert error">{modalError}</div>}
            {isEditing && canEdit && editDraft ? (
              <ProjectEditForm
                draft={editDraft}
                opportunities={opportunities}
                onChange={handleDraftChange}
                skillsText={skillsText}
                onSkillsChange={setSkillsText}
                onSave={() => void handleProjectSave()}
                onCancel={() => {
                  setEditDraft(selectedProject);
                  setSkillsText(formatSkillsText(selectedProject));
                  setIsEditing(false);
                  setModalError(null);
                }}
                isSaving={saving}
              />
            ) : (
              <>
                <ProjectDetails
                  project={selectedProject}
                  assignments={assignments}
                  opportunities={opportunities}
                  peopleByEmail={people.reduce<Record<string, Person>>((acc, person) => {
                    acc[person.email.toLowerCase()] = person;
                    return acc;
                  }, {})}
                />
                {matchError && <div className="alert info" style={{ marginTop: "1rem" }}>{matchError}</div>}
              </>
            )}
          </>
        )}
      </Modal>

      {/* Beach Candidates Modal with Pagination */}
      <Modal
        open={showBeachModal}
        onClose={handleCloseBeachModal}
        title={`Beach Candidates for ${selectedProject?.project_id || ""}`}
      >
        {beachMatches.length === 0 ? (
          <div className="alert info">
            No beach candidates found. The search did not return any matches.
          </div>
        ) : (
          <>
            {/* Pagination Controls */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
              <button
                type="button"
                className="ghost"
                onClick={handlePreviousCandidate}
                disabled={currentPage === 0}
                style={{ minWidth: "auto", padding: "0.5rem 1rem" }}
              >
                ← Previous
              </button>
              <span style={{ fontWeight: "600", fontSize: "1rem" }}>
                {currentPage + 1} / {beachMatches.length}
              </span>
              <button
                type="button"
                className="ghost"
                onClick={handleNextCandidate}
                disabled={currentPage === beachMatches.length - 1}
                style={{ minWidth: "auto", padding: "0.5rem 1rem" }}
              >
                Next →
              </button>
            </div>

            {/* Current Candidate Display */}
            {beachMatches[currentPage] && (
              <div className="panel" style={{ padding: "1.5rem" }}>
                <div style={{ marginBottom: "1.5rem", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <strong style={{ fontSize: "1.3rem" }}>{beachMatches[currentPage].beach_record.employee_name}</strong>
                    <div style={{ color: "var(--text-muted)", fontSize: "0.95rem", marginTop: "0.25rem" }}>
                      {beachMatches[currentPage].employee_email}
                    </div>
                  </div>
                  <div
                    style={{
                      backgroundColor: "#e0f2fe",
                      color: "#0369a1",
                      padding: "0.5rem 1rem",
                      borderRadius: "999px",
                      fontWeight: "700",
                      fontSize: "1.1rem",
                    }}
                  >
                    {beachMatches[currentPage].match_score}/100
                  </div>
                </div>

                {/* Staffing Status Banner */}
                {beachMatches[currentPage].beach_record.status === "ASSIGNED" && (
                  <div className="staffing-info-banner info">
                    <span>ℹ️</span>
                    <span>This person is currently assigned to another project.</span>
                  </div>
                )}
                {beachMatches[currentPage].beach_record.status === "HELD" && (
                  <div className="staffing-info-banner warning">
                    <span>⚠️</span>
                    <span>This person is held for another project.</span>
                  </div>
                )}

                <div className="details-grid" style={{ marginBottom: "1.5rem" }}>
                  <div className="detail-row">
                    <span className="detail-label">Role</span>
                    <span className="detail-value">{beachMatches[currentPage].beach_record.role ?? "-"}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Grade</span>
                    <span className="detail-value">{beachMatches[currentPage].beach_record.grade ?? "-"}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Office</span>
                    <span className="detail-value">{beachMatches[currentPage].beach_record.staffing_office ?? "-"}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Days Unassigned</span>
                    <span className="detail-value">{beachMatches[currentPage].beach_record.days_unassigned ?? "-"}</span>
                  </div>
                </div>

                <div style={{ marginBottom: "1.5rem" }}>
                  <h4 style={{ marginBottom: "0.75rem" }}>Matched Skills</h4>
                  {beachMatches[currentPage].matched_skills.length > 0 ? (
                    <div className="chip-row">
                      {beachMatches[currentPage].matched_skills.map((skill) => (
                        <span key={skill} className="chip">
                          {skill}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="muted">No specific skills matched.</p>
                  )}
                </div>

                <div style={{ marginBottom: "1.5rem" }}>
                  <h4 style={{ marginBottom: "0.75rem" }}>Summary</h4>
                  <p style={{ fontStyle: "italic", lineHeight: "1.5" }}>{beachMatches[currentPage].summary}</p>
                </div>

                <div style={{ display: "flex", gap: "1rem" }}>
                  <button
                    type="button"
                    className="primary"
                    onClick={() => void handleNominateCandidate(beachMatches[currentPage].employee_email)}
                  >
                    Nominate Candidate
                  </button>
                  <button
                    type="button"
                    className="ghost"
                    onClick={handleCloseBeachModal}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </Modal>

      {/* Create RR Modal */}
      <Modal
        open={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setCreateError(null);
        }}
        title="Create New RR"
      >
        <CreateRRForm
          opportunities={opportunities}
          onSuccess={(newProject) => {
            setProjects((prev) => [...prev, newProject]);
            setShowCreateModal(false);
            setCreateError(null);
          }}
          onError={(err) => setCreateError(err)}
          creating={creating}
          setCreating={setCreating}
          error={createError}
        />
      </Modal>
    </div>
  );
}

function CreateRRForm({
  opportunities,
  onSuccess,
  onError,
  creating,
  setCreating,
  error,
}: {
  opportunities: Opportunity[];
  onSuccess: (project: Project) => void;
  onError: (error: string) => void;
  creating: boolean;
  setCreating: (value: boolean) => void;
  error: string | null;
}) {
  const [formData, setFormData] = useState({
    title: "",
    status: "OPEN",
    start_date: "",
    end_date: "",
    delivery_unit: "",
    aws_role: "",
    aws_grade: "",
    tw_role: "",
    tw_grade: "",
    tw_archetype: "",
    notes: "",
    requested_hours: "",
    estimated_skills: "",
    opportunity_id: "",
  });

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    onError("");

    try {
      // Parse estimated_skills from comma-separated string
      const skillsArray = formData.estimated_skills
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);

      const payload: Partial<Project> = {
        title: formData.title,
        status: formData.status,
        start_date: formData.start_date || undefined,
        end_date: formData.end_date || undefined,
        delivery_unit: formData.delivery_unit || undefined,
        aws_role: formData.aws_role || undefined,
        aws_grade: formData.aws_grade || undefined,
        tw_role: formData.tw_role || undefined,
        tw_grade: formData.tw_grade || undefined,
        tw_archetype: formData.tw_archetype || undefined,
        notes: formData.notes || undefined,
        requested_hours: formData.requested_hours ? Number(formData.requested_hours) : undefined,
        estimated_skills: skillsArray.length > 0 ? skillsArray : undefined,
        opportunity_id: formData.opportunity_id || undefined,
      };

      const newProject = await createProject(payload as Project);
      onSuccess(newProject);
    } catch (err) {
      onError(getErrorAlertMessage("create RR", err));
    } finally {
      setCreating(false);
    }
  };

  return (
    <form className="form-grid" onSubmit={handleSubmit}>
      <label className="span-2">
        Title <span style={{ color: "#b91c1c" }}>*</span>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => handleChange("title", e.target.value)}
          required
          placeholder="e.g. Senior Cloud Engineer"
        />
      </label>

      <label>
        Status
        <select value={formData.status} onChange={(e) => handleChange("status", e.target.value)}>
          {PROJECT_STATUSES.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </label>

      <label>
        Requested Hours
        <input
          type="number"
          step="0.1"
          value={formData.requested_hours}
          onChange={(e) => handleChange("requested_hours", e.target.value)}
          placeholder="e.g. 324"
        />
      </label>

      <label>
        Start Date
        <input
          type="date"
          value={formData.start_date}
          onChange={(e) => handleChange("start_date", e.target.value)}
        />
      </label>

      <label>
        End Date
        <input
          type="date"
          value={formData.end_date}
          onChange={(e) => handleChange("end_date", e.target.value)}
        />
      </label>

      <label>
        Delivery Unit
        <input
          type="text"
          value={formData.delivery_unit}
          onChange={(e) => handleChange("delivery_unit", e.target.value)}
          placeholder="e.g. US, EMEA, APAC"
        />
      </label>

      <label>
        AWS Role
        <input
          type="text"
          value={formData.aws_role}
          onChange={(e) => handleChange("aws_role", e.target.value)}
          placeholder="e.g. Cloud Engineer"
        />
      </label>

      <label>
        AWS Grade
        <input
          type="text"
          value={formData.aws_grade}
          onChange={(e) => handleChange("aws_grade", e.target.value)}
          placeholder="e.g. Senior"
        />
      </label>

      <label>
        TW Role
        <input
          type="text"
          value={formData.tw_role}
          onChange={(e) => handleChange("tw_role", e.target.value)}
          placeholder="e.g. Lead Developer"
        />
      </label>

      <label>
        TW Grade
        <input
          type="text"
          value={formData.tw_grade}
          onChange={(e) => handleChange("tw_grade", e.target.value)}
          placeholder="e.g. P3"
        />
      </label>

      <label className="span-2">
        TW Archetype
        <input
          type="text"
          value={formData.tw_archetype}
          onChange={(e) => handleChange("tw_archetype", e.target.value)}
          placeholder="e.g. Developer, Architect"
        />
      </label>

      <label className="span-2">
        Estimated Skills (comma separated)
        <textarea
          value={formData.estimated_skills}
          onChange={(e) => handleChange("estimated_skills", e.target.value)}
          rows={3}
          placeholder="e.g. Python, AWS, Docker, Kubernetes"
        />
      </label>

      <label className="span-2">
        Notes
        <textarea
          value={formData.notes}
          onChange={(e) => handleChange("notes", e.target.value)}
          rows={4}
          placeholder="Additional notes and requirements..."
        />
      </label>

      <label className="span-2">
        Opportunity ID (optional)
        <input
          type="text"
          list="opportunity-suggestions"
          value={formData.opportunity_id}
          onChange={(e) => handleChange("opportunity_id", e.target.value)}
          placeholder="e.g. OP-1001 or start typing to search..."
        />
        <datalist id="opportunity-suggestions">
          {opportunities.map((opp) => (
            <option key={opp.opportunity_id} value={opp.opportunity_id}>
              {opp.name}
            </option>
          ))}
        </datalist>
      </label>

      {error && (
        <div className="span-2 alert error">{error}</div>
      )}

      <div className="form-actions span-2">
        <button type="submit" className="primary" disabled={creating}>
          {creating ? "Creating…" : "Create RR"}
        </button>
      </div>
    </form>
  );
}

function ProjectDetails({
  project,
  assignments,
  opportunities,
  peopleByEmail,
}: {
  project: Project;
  assignments: Assignment[];
  opportunities: Opportunity[];
  peopleByEmail: Record<string, Person>;
}) {
  const summary = project.summary as AiProjectSummary | undefined;

  // Find the associated opportunity if one exists
  const opportunity = project.opportunity_id
    ? opportunities.find(opp => opp.opportunity_id === project.opportunity_id)
    : undefined;

  const detailPairs: Array<[string, string]> = [
    ["RR", project.project_id],
    ["Title", project.title],
    ["Status", project.status ?? "-"] ,
    ["Opportunity", opportunity ? `${opportunity.opportunity_id} - ${opportunity.name}` : "-"],
    ["Customer", opportunity?.customer_legal_name ?? "-"],
    ["Start Date", project.start_date ?? "-"],
    ["End Date", project.end_date ?? "-"],
    ["Delivery Unit", project.delivery_unit ?? "-"],
    ["AWS Role", summary?.aws_role ?? project.aws_role ?? "-"],
    ["AWS Grade", summary?.aws_grade ?? project.aws_grade ?? "-"],
    ["TW Role", summary?.tw_role ?? project.tw_role ?? "-"],
    ["TW Grade", summary?.tw_grade ?? project.tw_grade ?? "-"],
    ["TW Archetype", summary?.tw_archetype ?? project.tw_archetype ?? "-"],
    ["FTE Allocation", summary?.fte_allocation ?? project.fte_allocation ?? "-"],
    ["Requested Hours", formatNumber(summary?.requested_hours ?? project.requested_hours)],
    ["Avg Week Hours", formatNumber(summary?.avg_week_hours ?? project.avg_week_hours)],
  ];

  const skills = toStringArray(summary?.estimated_skills ?? project.estimated_skills);
  const projectAssignments = assignments.filter((assignment) => assignment.project_id === project.project_id);
  const candidates = projectAssignments.map((assignment) => {
    const email = assignment.person_email;
    const person = peopleByEmail[email.toLowerCase()];
    const name = person?.name;
    return { label: name ? `${name} (${email})` : email, status: assignment.status };
  });
  const notes = summary?.notes ?? project.notes ?? project.description ?? "No additional notes.";

  return (
    <div className="project-details">
      <div className="details-grid">
        {detailPairs.map(([label, value]) => (
          <div key={label} className="detail-row">
            <span className="detail-label">{label}</span>
            <span className="detail-value">{value ?? "-"}</span>
          </div>
        ))}
      </div>
      <div className="detail-block">
        <h4>Estimated Skills</h4>
        {skills.length > 0 ? (
          <div className="chip-row">
            {skills.map((skill) => (
              <span key={skill} className="chip">
                {skill}
              </span>
            ))}
          </div>
        ) : (
          <p className="muted">No specific skills captured.</p>
        )}
      </div>
      <div className="detail-block">
        <h4>Proposed Candidates</h4>
        {candidates.length > 0 ? (
          <ul>
            {candidates.map((candidate) => (
              <li key={candidate.label}>
                {candidate.status === "ACCEPTED" ? <strong>{candidate.label}</strong> : candidate.label}
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">No candidates proposed yet.</p>
        )}
      </div>
      <div className="detail-block">
        <h4>Notes</h4>
        <p>{notes}</p>
      </div>
    </div>
  );
}

function ProjectEditForm({
  draft,
  opportunities,
  onChange,
  skillsText,
  onSkillsChange,
  onSave,
  onCancel,
  isSaving,
}: {
  draft: Project;
  opportunities: Opportunity[];
  onChange: (field: keyof Project, value: string | number | null | undefined) => void;
  skillsText: string;
  onSkillsChange: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
}) {
  return (
    <form
      className="form-grid"
      onSubmit={(event) => {
        event.preventDefault();
        onSave();
      }}
    >
      <label className="span-2">
        Title
        <input type="text" value={draft.title ?? ""} onChange={(event) => onChange("title", event.target.value)} required />
      </label>
      <label>
        Status
        <select value={draft.status ?? ""} onChange={(event) => onChange("status", event.target.value)}>
          {PROJECT_STATUSES.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </label>
      <label>
        Requested Hours
        <input
          type="number"
          step="0.1"
          value={draft.requested_hours ?? ""}
          onChange={(event) => onChange("requested_hours", event.target.value ? Number(event.target.value) : null)}
        />
      </label>
      <label>
        Start Date
        <input type="date" value={draft.start_date ?? ""} onChange={(event) => onChange("start_date", event.target.value)} />
      </label>
      <label>
        End Date
        <input type="date" value={draft.end_date ?? ""} onChange={(event) => onChange("end_date", event.target.value)} />
      </label>
      <label>
        Delivery Unit
        <input type="text" value={draft.delivery_unit ?? ""} onChange={(event) => onChange("delivery_unit", event.target.value)} />
      </label>
      <label>
        AWS Role
        <input type="text" value={draft.aws_role ?? ""} onChange={(event) => onChange("aws_role", event.target.value)} />
      </label>
      <label>
        AWS Grade
        <input type="text" value={draft.aws_grade ?? ""} onChange={(event) => onChange("aws_grade", event.target.value)} />
      </label>
      <label>
        TW Role
        <input type="text" value={draft.tw_role ?? ""} onChange={(event) => onChange("tw_role", event.target.value)} />
      </label>
      <label>
        TW Grade
        <input type="text" value={draft.tw_grade ?? ""} onChange={(event) => onChange("tw_grade", event.target.value)} />
      </label>
      <label>
        TW Archetype
        <input type="text" value={draft.tw_archetype ?? ""} onChange={(event) => onChange("tw_archetype", event.target.value)} />
      </label>
      <label className="span-2">
        Estimated Skills (comma separated)
        <textarea value={skillsText} rows={3} onChange={(event) => onSkillsChange(event.target.value)} />
      </label>
      <label className="span-2">
        Notes
        <textarea value={draft.notes ?? ""} rows={3} onChange={(event) => onChange("notes", event.target.value)} />
      </label>
      <label className="span-2">
        Opportunity ID (optional)
        <input
          type="text"
          list="opportunity-suggestions-edit"
          value={draft.opportunity_id ?? ""}
          onChange={(event) => onChange("opportunity_id", event.target.value || undefined)}
          placeholder="e.g. OP-1001 or start typing to search..."
        />
        <datalist id="opportunity-suggestions-edit">
          {opportunities.map((opp) => (
            <option key={opp.opportunity_id} value={opp.opportunity_id}>
              {opp.name}
            </option>
          ))}
        </datalist>
      </label>
      <div className="form-actions span-2">
        <button type="submit" className="primary" disabled={isSaving}>
          {isSaving ? "Saving…" : "Save Changes"}
        </button>
        <button type="button" className="ghost" onClick={onCancel} disabled={isSaving}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function formatBoolean(value: boolean | undefined): string {
  if (value === undefined) return "-";
  return value ? "Yes" : "No";
}

function formatNumber(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return "-";
  const numeric = typeof value === "string" ? Number(value) : value;
  if (Number.isNaN(numeric)) return "-";
  return numeric.toFixed(2);
}

function toStringArray(value: unknown): string[] {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((entry) => (typeof entry === "string" ? entry.trim() : String(entry)))
      .filter((entry) => entry.length > 0);
  }
  if (typeof value === "string") {
    return value
      .split(/[,\n]/)
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0);
  }
  return [];
}

function formatSkillsText(project?: Project | null) {
  return project?.estimated_skills?.join(", ") ?? "";
}
